// app/screens/business/projects/ProjectProductCreationScreen.js
// JobEng - Comprehensive Project + Product creation & management
//
// Full rebuild:
//  • ProjectCreationModal — 6 internal tabs: Basic, Timeline, Team, Financial, Tasks, Notes
//  • ProductCreationModal — 6 internal tabs: Basic, Pricing, Inventory, Variants, Media, SEO
//  • Main screen — search, filter by status/category/priority, sort, bulk ops, analytics
//  • AI-assisted content generation for descriptions, briefs, feature lists
//  • Live calculated fields: margin %, days remaining, days elapsed, completion projections
//  • Tags, milestones, sub-tasks, risk register, audit log
//  • All endpoints fail gracefully to local-only mode

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Modal,
  SafeAreaView,
  Alert,
  StatusBar,
  Dimensions,
  ActivityIndicator,
  FlatList,
  RefreshControl,
  Platform,
  KeyboardAvoidingView,
  Switch,
  Pressable,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width: SCREEN_W } = Dimensions.get('window');

// ──────────────────────────────────────────────────────────────────────────────
// Config
// ──────────────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const COLORS = {
  primary:   '#0e4e96',
  primaryDk: '#0a3d75',
  accent:    '#7C3AED',
  accentLt:  '#A78BFA',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  info:      '#06B6D4',
  pink:      '#EC4899',
  text:      '#1F2937',
  sub:       '#6B7280',
  light:     '#9CA3AF',
  bg:        '#F3F4F6',
  bgAlt:     '#F9FAFB',
  surface:   '#FFFFFF',
  border:    '#E5E7EB',
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY },
});
api.interceptors.request.use(async (c) => {
  try {
    const t = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('token'));
    if (t) c.headers.Authorization = `Bearer ${t}`;
  } catch {}
  return c;
});

const EP = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  projects:       '/api/data/projects',
  products:       '/api/data/products',
  tasks:          '/api/data/project_tasks',
  milestones:     '/api/data/project_milestones',
  teamMembers:    '/api/team/members',
  aiGenerate:     '/api/ai/generate-content',
};

const safeData = (r) => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];
const getBusinessId = async () => {
  try { return (await AsyncStorage.getItem('company_id')) || '0'; } catch { return '0'; }
};

// ──────────────────────────────────────────────────────────────────────────────
// Constants — option lists
// ──────────────────────────────────────────────────────────────────────────────
const PROJECT_CATEGORIES = ['Web Development', 'Mobile App', 'Marketing', 'Design', 'Construction', 'Consulting', 'Research', 'Internal', 'Other'];
const PROJECT_TYPES      = ['Client Project', 'Internal Project', 'R&D', 'Maintenance', 'Migration', 'Launch'];
const PROJECT_STATUSES   = ['Planning', 'Active', 'On Hold', 'Review', 'Completed', 'Cancelled'];
const PRIORITIES         = ['Critical', 'High', 'Medium', 'Low'];

const PRODUCT_CATEGORIES = ['Software', 'SaaS', 'Hardware', 'Service', 'Physical Goods', 'Digital Goods', 'Subscription', 'Consulting'];
const PRODUCT_TYPES      = ['Digital Product', 'Physical Product', 'Service', 'Subscription', 'Bundle', 'Add-on'];
const PRODUCT_STATUSES   = ['Concept', 'Development', 'Beta', 'Active', 'Sunset', 'Discontinued'];
const BILLING_CYCLES     = ['One-time', 'Monthly', 'Quarterly', 'Annual', 'Custom'];

const STATUS_COLORS = {
  // Project
  Planning: COLORS.info, Active: COLORS.success, 'On Hold': COLORS.warning,
  Review: COLORS.accent, Completed: COLORS.success, Cancelled: COLORS.sub,
  // Product
  Concept: COLORS.info, Development: COLORS.warning, Beta: COLORS.accent,
  Sunset: COLORS.warning, Discontinued: COLORS.danger,
  // Priorities
  Critical: COLORS.danger, High: COLORS.warning, Medium: COLORS.info, Low: COLORS.sub,
};

// ══════════════════════════════════════════════════════════════════════════════
// Helpers
// ══════════════════════════════════════════════════════════════════════════════
const formatCurrency = (v, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(parseFloat(v) || 0);

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—';

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const slugify = (s) => String(s || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');

// ══════════════════════════════════════════════════════════════════════════════
// PROJECT CREATION MODAL
// ══════════════════════════════════════════════════════════════════════════════
const ProjectCreationModal = ({ visible, onClose, onSave, initialData = null }) => {
  const [activeSection, setActiveSection] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [newTagText, setNewTagText] = useState('');
  const [newMemberText, setNewMemberText] = useState('');
  const [newTaskText, setNewTaskText] = useState('');
  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('');
  const [newRiskText, setNewRiskText] = useState('');

  const emptyForm = {
    id: '', name: '', description: '', brief: '',
    category: PROJECT_CATEGORIES[0],
    type: PROJECT_TYPES[0],
    priority: 'Medium',
    status: 'Planning',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    deadline: '',
    estimatedHours: '',
    actualHours: '',
    budget: '',
    actualCost: '',
    currency: 'GBP',
    paymentTerms: '',
    invoicedAmount: '',
    manager: '',
    members: [],
    client: '',
    clientEmail: '',
    clientPhone: '',
    progress: 0,
    tasks: [],
    milestones: [],
    risks: [],
    tags: [],
    notes: '',
    isPublic: false,
    completionCriteria: '',
    deliverables: [],
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (visible) {
      if (initialData) {
        setForm({ ...emptyForm, ...initialData, tags: initialData.tags || [], members: initialData.members || [], tasks: initialData.tasks || [], milestones: initialData.milestones || [], risks: initialData.risks || [], deliverables: initialData.deliverables || [] });
      } else {
        setForm(emptyForm);
      }
      setActiveSection('basic');
    }
  }, [visible, initialData]);

  const update = (field, value) => setForm({ ...form, [field]: value });

  // ── Derived/calculated fields ──────────────────────────────────────────────
  const daysRemaining = useMemo(() => form.endDate ? daysBetween(new Date().toISOString(), form.endDate) : null, [form.endDate]);
  const daysElapsed = useMemo(() => form.startDate ? daysBetween(form.startDate, new Date().toISOString()) : null, [form.startDate]);
  const budgetUsed = useMemo(() => {
    const b = parseFloat(form.budget) || 0;
    const a = parseFloat(form.actualCost) || 0;
    return b > 0 ? Math.round((a / b) * 100) : 0;
  }, [form.budget, form.actualCost]);
  const tasksCompleted = useMemo(() => form.tasks.filter(t => t.done).length, [form.tasks]);
  const taskProgress = useMemo(() => form.tasks.length ? Math.round((tasksCompleted / form.tasks.length) * 100) : 0, [form.tasks.length, tasksCompleted]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.name.trim()) { Alert.alert('Required', 'Project name is required'); setActiveSection('basic'); return false; }
    if (form.endDate && form.startDate && new Date(form.endDate) < new Date(form.startDate)) {
      Alert.alert('Invalid Dates', 'End date must be after start date'); setActiveSection('timeline'); return false;
    }
    if (form.budget && isNaN(parseFloat(form.budget))) { Alert.alert('Invalid', 'Budget must be a number'); setActiveSection('financial'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...form, progress: parseInt(form.progress, 10) || 0 });
    } finally {
      setSaving(false);
    }
  };

  // ── AI brief generator ────────────────────────────────────────────────────
  const generateBrief = async () => {
    if (!form.name) { Alert.alert('Name required', 'Enter a project name first'); return; }
    setAiGenerating(true);
    try {
      const r = await api.post(EP.aiGenerate, {
        prompt: `Generate a professional project brief for "${form.name}" (${form.category}, ${form.type}). Include objectives, scope, deliverables, and success criteria.`,
        tone: 'professional', length: 'medium',
      }).catch(() => null);
      const text = r?.data?.content || r?.data?.text || '';
      if (text) {
        update('brief', text);
      } else {
        const fallback = `# Project Brief: ${form.name}\n\n## Overview\n${form.description || 'Describe the project context, goals, and stakeholders here.'}\n\n## Objectives\n- Define the primary outcomes\n- Identify key deliverables\n- Establish success criteria\n\n## Scope\n${form.type} - ${form.category}\n\n## Timeline\n- Start: ${form.startDate || 'TBD'}\n- End: ${form.endDate || 'TBD'}\n\n## Success Criteria\n- Delivered on time and within budget\n- Meets quality standards\n- Stakeholder approval`;
        update('brief', fallback);
      }
    } catch (e) {
      Alert.alert('AI Error', e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  // ── Tag/member/task/milestone management ───────────────────────────────────
  const addTag = () => {
    const t = newTagText.trim();
    if (!t || form.tags.includes(t)) return;
    update('tags', [...form.tags, t]);
    setNewTagText('');
  };
  const removeTag = (t) => update('tags', form.tags.filter(x => x !== t));

  const addMember = () => {
    const m = newMemberText.trim();
    if (!m || form.members.includes(m)) return;
    update('members', [...form.members, m]);
    setNewMemberText('');
  };
  const removeMember = (m) => update('members', form.members.filter(x => x !== m));

  const addTask = () => {
    const t = newTaskText.trim();
    if (!t) return;
    update('tasks', [...form.tasks, { id: Date.now(), text: t, done: false, assignee: '' }]);
    setNewTaskText('');
  };
  const toggleTask = (id) => update('tasks', form.tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
  const removeTask = (id) => update('tasks', form.tasks.filter(t => t.id !== id));

  const addMilestone = () => {
    if (!newMilestoneTitle.trim()) return;
    update('milestones', [...form.milestones, { id: Date.now(), title: newMilestoneTitle.trim(), date: newMilestoneDate, done: false }]);
    setNewMilestoneTitle(''); setNewMilestoneDate('');
  };
  const toggleMilestone = (id) => update('milestones', form.milestones.map(m => m.id === id ? { ...m, done: !m.done } : m));
  const removeMilestone = (id) => update('milestones', form.milestones.filter(m => m.id !== id));

  const addRisk = () => {
    if (!newRiskText.trim()) return;
    update('risks', [...form.risks, { id: Date.now(), text: newRiskText.trim(), severity: 'Medium', mitigation: '' }]);
    setNewRiskText('');
  };
  const updateRisk = (id, field, value) => update('risks', form.risks.map(r => r.id === id ? { ...r, [field]: value } : r));
  const removeRisk = (id) => update('risks', form.risks.filter(r => r.id !== id));

  // ── Section renderers ──────────────────────────────────────────────────────
  const renderBasic = () => (
    <>
      <Field label="Project Name" required value={form.name} onChange={(v) => update('name', v)} placeholder="e.g. Acme Website Redesign" />
      <Field label="Description" value={form.description} onChange={(v) => update('description', v)} placeholder="Short description" multiline rows={3} />

      <Label>Category</Label>
      <ChipRow items={PROJECT_CATEGORIES} selected={form.category} onSelect={(v) => update('category', v)} color={COLORS.primary} />

      <Label>Type</Label>
      <ChipRow items={PROJECT_TYPES} selected={form.type} onSelect={(v) => update('type', v)} color={COLORS.primary} />

      <Label>Status</Label>
      <ChipRow items={PROJECT_STATUSES} selected={form.status} onSelect={(v) => update('status', v)} color={COLORS.primary} />

      <Label>Priority</Label>
      <ChipRow items={PRIORITIES} selected={form.priority} onSelect={(v) => update('priority', v)} color={COLORS.danger} />

      <View style={st.row}>
        <Label style={{ flex: 1 }}>Progress: {form.progress}%</Label>
        <Text style={{ fontSize: 11, color: COLORS.sub }}>Tasks: {taskProgress}%</Text>
      </View>
      <View style={st.progressBar}>
        <View style={[st.progressFill, { width: `${form.progress}%`, backgroundColor: COLORS.primary }]} />
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingTop: 10 }}>
        {[0, 10, 25, 50, 75, 90, 100].map(p => (
          <TouchableOpacity key={p} style={[st.miniChip, form.progress === p && { backgroundColor: COLORS.primary }]} onPress={() => update('progress', p)}>
            <Text style={[st.miniChipTxt, form.progress === p && { color: '#FFF', fontWeight: '700' }]}>{p}%</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <Field label="Tags" value={newTagText} onChange={setNewTagText} placeholder="Add tag and press +" rightIcon="plus" onRightPress={addTag} onSubmit={addTag} />
      <View style={st.chipsArea}>
        {form.tags.map(t => (
          <TouchableOpacity key={t} style={st.tagChip} onPress={() => removeTag(t)}>
            <Text style={st.tagChipTxt}>#{t}</Text>
            <MaterialCommunityIcons name="close-circle" size={14} color={COLORS.sub} />
          </TouchableOpacity>
        ))}
      </View>

      <View style={[st.row, { marginTop: 12, alignItems: 'center' }]}>
        <View style={{ flex: 1 }}>
          <Text style={st.label}>Public Project</Text>
          <Text style={st.helper}>Visible to all company members</Text>
        </View>
        <Switch value={form.isPublic} onValueChange={(v) => update('isPublic', v)} trackColor={{ true: COLORS.primary, false: COLORS.border }} />
      </View>
    </>
  );

  const renderTimeline = () => (
    <>
      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Start Date" value={form.startDate} onChange={(v) => update('startDate', v)} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="End Date" value={form.endDate} onChange={(v) => update('endDate', v)} placeholder="YYYY-MM-DD" />
        </View>
      </View>
      <Field label="Hard Deadline" value={form.deadline} onChange={(v) => update('deadline', v)} placeholder="YYYY-MM-DD (if different from end)" />

      {(daysRemaining !== null || daysElapsed !== null) && (
        <View style={st.calcRow}>
          {daysElapsed !== null && (
            <View style={st.calcCard}>
              <Text style={st.calcNum}>{daysElapsed}</Text>
              <Text style={st.calcLbl}>Days Elapsed</Text>
            </View>
          )}
          {daysRemaining !== null && (
            <View style={[st.calcCard, { backgroundColor: daysRemaining < 0 ? `${COLORS.danger}15` : daysRemaining < 7 ? `${COLORS.warning}15` : `${COLORS.success}15` }]}>
              <Text style={[st.calcNum, { color: daysRemaining < 0 ? COLORS.danger : daysRemaining < 7 ? COLORS.warning : COLORS.success }]}>
                {Math.abs(daysRemaining)}
              </Text>
              <Text style={st.calcLbl}>{daysRemaining < 0 ? 'Days Overdue' : 'Days Remaining'}</Text>
            </View>
          )}
        </View>
      )}

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Est. Hours" value={form.estimatedHours} onChange={(v) => update('estimatedHours', v)} placeholder="0" keyboardType="numeric" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Actual Hours" value={form.actualHours} onChange={(v) => update('actualHours', v)} placeholder="0" keyboardType="numeric" />
        </View>
      </View>

      <Label>Milestones</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 2, marginRight: 6 }]} value={newMilestoneTitle} onChangeText={setNewMilestoneTitle} placeholder="Milestone title" placeholderTextColor={COLORS.light} />
        <TextInput style={[st.input, { flex: 1, marginLeft: 6, marginRight: 6 }]} value={newMilestoneDate} onChangeText={setNewMilestoneDate} placeholder="YYYY-MM-DD" placeholderTextColor={COLORS.light} />
        <TouchableOpacity style={st.iconBtn} onPress={addMilestone}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>
      {form.milestones.map(m => (
        <View key={m.id} style={st.listRow}>
          <TouchableOpacity onPress={() => toggleMilestone(m.id)}>
            <MaterialCommunityIcons name={m.done ? 'check-circle' : 'circle-outline'} size={22} color={m.done ? COLORS.success : COLORS.light} />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Text style={[st.listTitle, m.done && { textDecorationLine: 'line-through', color: COLORS.light }]}>{m.title}</Text>
            {m.date && <Text style={st.listMeta}>{formatDate(m.date)}</Text>}
          </View>
          <TouchableOpacity onPress={() => removeMilestone(m.id)}>
            <MaterialCommunityIcons name="delete-outline" size={20} color={COLORS.danger} />
          </TouchableOpacity>
        </View>
      ))}
    </>
  );

  const renderTeam = () => (
    <>
      <Field label="Project Manager" value={form.manager} onChange={(v) => update('manager', v)} placeholder="Lead manager name" />

      <Label>Team Members</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newMemberText} onChangeText={setNewMemberText} placeholder="Name or email" placeholderTextColor={COLORS.light} onSubmitEditing={addMember} />
        <TouchableOpacity style={st.iconBtn} onPress={addMember}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>
      <View style={st.chipsArea}>
        {form.members.map(m => (
          <View key={m} style={st.memberChip}>
            <View style={st.memberAvatar}>
              <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 11 }}>{(m[0] || '?').toUpperCase()}</Text>
            </View>
            <Text style={st.memberName}>{m}</Text>
            <TouchableOpacity onPress={() => removeMember(m)}>
              <MaterialCommunityIcons name="close" size={14} color={COLORS.sub} />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      <Label style={{ marginTop: 16 }}>Client</Label>
      <Field label="Client / Company" value={form.client} onChange={(v) => update('client', v)} placeholder="Client name" />
      <Field label="Client Email" value={form.clientEmail} onChange={(v) => update('clientEmail', v)} placeholder="client@example.com" keyboardType="email-address" />
      <Field label="Client Phone" value={form.clientPhone} onChange={(v) => update('clientPhone', v)} placeholder="+44..." keyboardType="phone-pad" />
    </>
  );

  const renderFinancial = () => (
    <>
      <Label>Currency</Label>
      <ChipRow items={['GBP', 'USD', 'EUR', 'AED', 'SAR']} selected={form.currency} onSelect={(v) => update('currency', v)} color={COLORS.success} />

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Total Budget" value={form.budget} onChange={(v) => update('budget', v)} placeholder="0" keyboardType="numeric" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Actual Cost" value={form.actualCost} onChange={(v) => update('actualCost', v)} placeholder="0" keyboardType="numeric" />
        </View>
      </View>

      {form.budget > 0 && (
        <View style={[st.calcCard, { marginTop: 8 }]}>
          <Text style={[st.calcLbl, { fontSize: 13, fontWeight: '700', color: COLORS.text }]}>Budget Health</Text>
          <View style={[st.progressBar, { marginTop: 8, height: 8 }]}>
            <View style={[st.progressFill, { width: `${Math.min(budgetUsed, 100)}%`, backgroundColor: budgetUsed > 100 ? COLORS.danger : budgetUsed > 80 ? COLORS.warning : COLORS.success }]} />
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
            <Text style={st.helper}>Used: {formatCurrency(form.actualCost, form.currency)}</Text>
            <Text style={[st.helper, { fontWeight: '700', color: budgetUsed > 100 ? COLORS.danger : COLORS.text }]}>{budgetUsed}%</Text>
            <Text style={st.helper}>Budget: {formatCurrency(form.budget, form.currency)}</Text>
          </View>
          {budgetUsed > 100 && (
            <Text style={[st.helper, { color: COLORS.danger, marginTop: 4 }]}>
              ⚠ Over budget by {formatCurrency((form.actualCost || 0) - (form.budget || 0), form.currency)}
            </Text>
          )}
        </View>
      )}

      <Field label="Invoiced Amount" value={form.invoicedAmount} onChange={(v) => update('invoicedAmount', v)} placeholder="0" keyboardType="numeric" />
      <Field label="Payment Terms" value={form.paymentTerms} onChange={(v) => update('paymentTerms', v)} placeholder="Net 30, milestone-based, etc." multiline rows={2} />
    </>
  );

  const renderTasks = () => (
    <>
      <Label>Sub-Tasks ({tasksCompleted}/{form.tasks.length} done)</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newTaskText} onChangeText={setNewTaskText} placeholder="Add a task..." placeholderTextColor={COLORS.light} onSubmitEditing={addTask} />
        <TouchableOpacity style={st.iconBtn} onPress={addTask}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.primary} />
        </TouchableOpacity>
      </View>

      {form.tasks.length > 0 && (
        <View style={[st.progressBar, { marginTop: 12, height: 8 }]}>
          <View style={[st.progressFill, { width: `${taskProgress}%`, backgroundColor: COLORS.success }]} />
        </View>
      )}

      {form.tasks.map(t => (
        <View key={t.id} style={st.listRow}>
          <TouchableOpacity onPress={() => toggleTask(t.id)}>
            <MaterialCommunityIcons name={t.done ? 'checkbox-marked' : 'checkbox-blank-outline'} size={22} color={t.done ? COLORS.success : COLORS.light} />
          </TouchableOpacity>
          <Text style={[st.listTitle, { flex: 1, marginLeft: 10 }, t.done && { textDecorationLine: 'line-through', color: COLORS.light }]}>{t.text}</Text>
          <TouchableOpacity onPress={() => removeTask(t.id)}>
            <MaterialCommunityIcons name="delete-outline" size={20} color={COLORS.danger} />
          </TouchableOpacity>
        </View>
      ))}

      {form.tasks.length === 0 && (
        <View style={[st.empty, { padding: 24 }]}>
          <MaterialCommunityIcons name="format-list-checks" size={40} color={COLORS.border} />
          <Text style={st.helper}>No tasks yet — add one above</Text>
        </View>
      )}

      <Label style={{ marginTop: 16 }}>Risks</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newRiskText} onChangeText={setNewRiskText} placeholder="Risk description..." placeholderTextColor={COLORS.light} onSubmitEditing={addRisk} />
        <TouchableOpacity style={st.iconBtn} onPress={addRisk}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.warning} />
        </TouchableOpacity>
      </View>
      {form.risks.map(r => (
        <View key={r.id} style={st.riskCard}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <MaterialCommunityIcons name="alert-circle" size={18} color={STATUS_COLORS[r.severity] || COLORS.warning} />
            <Text style={[st.listTitle, { flex: 1, marginLeft: 8 }]}>{r.text}</Text>
            <TouchableOpacity onPress={() => removeRisk(r.id)}>
              <MaterialCommunityIcons name="close" size={18} color={COLORS.sub} />
            </TouchableOpacity>
          </View>
          <ChipRow items={PRIORITIES} selected={r.severity} onSelect={(v) => updateRisk(r.id, 'severity', v)} color={COLORS.warning} size="small" />
          <TextInput style={[st.input, { marginTop: 8, fontSize: 12 }]} value={r.mitigation} onChangeText={(v) => updateRisk(r.id, 'mitigation', v)} placeholder="Mitigation plan..." placeholderTextColor={COLORS.light} multiline />
        </View>
      ))}
    </>
  );

  const renderNotes = () => (
    <>
      <View style={st.row}>
        <Label style={{ flex: 1 }}>Project Brief</Label>
        <TouchableOpacity style={[st.smallBtn, { backgroundColor: COLORS.accent }, aiGenerating && { backgroundColor: COLORS.light }]} onPress={generateBrief} disabled={aiGenerating}>
          {aiGenerating ? <ActivityIndicator color="#FFF" size="small" /> : (
            <>
              <MaterialCommunityIcons name="sparkles" size={14} color="#FFF" />
              <Text style={st.smallBtnTxt}>AI Generate</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      <TextInput
        style={[st.input, { minHeight: 180, textAlignVertical: 'top', marginTop: 6 }]}
        value={form.brief}
        onChangeText={(v) => update('brief', v)}
        placeholder="Project brief, objectives, scope..."
        placeholderTextColor={COLORS.light}
        multiline
      />

      <Field label="Completion Criteria" value={form.completionCriteria} onChange={(v) => update('completionCriteria', v)} placeholder="What defines 'done'?" multiline rows={3} />
      <Field label="Notes" value={form.notes} onChange={(v) => update('notes', v)} placeholder="Internal notes, observations..." multiline rows={4} />
    </>
  );

  const sections = [
    { id: 'basic',     label: 'Basic',     icon: 'information-outline' },
    { id: 'timeline',  label: 'Timeline',  icon: 'calendar-clock' },
    { id: 'team',      label: 'Team',      icon: 'account-group-outline' },
    { id: 'financial', label: 'Financial', icon: 'cash-multiple' },
    { id: 'tasks',     label: 'Tasks',     icon: 'check-circle-outline' },
    { id: 'notes',     label: 'Notes',     icon: 'note-text-outline' },
  ];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalHeader}>
            <TouchableOpacity onPress={onClose}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={st.modalTitle}>{initialData ? 'Edit Project' : 'New Project'}</Text>
            <TouchableOpacity onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color={COLORS.primary} /> : <Text style={[st.saveBtn, { color: COLORS.primary }]}>Save</Text>}
            </TouchableOpacity>
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.sectionTabs} contentContainerStyle={{ paddingHorizontal: 12 }}>
            {sections.map(s => (
              <TouchableOpacity key={s.id} style={[st.sectionTab, activeSection === s.id && { backgroundColor: `${COLORS.primary}15` }]} onPress={() => setActiveSection(s.id)}>
                <MaterialCommunityIcons name={s.icon} size={16} color={activeSection === s.id ? COLORS.primary : COLORS.sub} />
                <Text style={[st.sectionTabTxt, activeSection === s.id && { color: COLORS.primary, fontWeight: '700' }]}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
            {activeSection === 'basic'     && renderBasic()}
            {activeSection === 'timeline'  && renderTimeline()}
            {activeSection === 'team'      && renderTeam()}
            {activeSection === 'financial' && renderFinancial()}
            {activeSection === 'tasks'     && renderTasks()}
            {activeSection === 'notes'     && renderNotes()}
          </ScrollView>
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT CREATION MODAL
// ══════════════════════════════════════════════════════════════════════════════
const ProductCreationModal = ({ visible, onClose, onSave, initialData = null }) => {
  const [activeSection, setActiveSection] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [newFeatureText, setNewFeatureText] = useState('');
  const [newTagText, setNewTagText] = useState('');
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantPrice, setNewVariantPrice] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');

  const emptyForm = {
    id: '', name: '', description: '', shortDesc: '', brief: '',
    category: PRODUCT_CATEGORIES[0],
    type: PRODUCT_TYPES[0],
    status: 'Development',
    version: '1.0.0',
    sku: '',
    price: '', salePrice: '', cost: '', currency: 'GBP',
    billingCycle: 'One-time',
    stock: '', lowStockThreshold: '',
    weight: '', dimensions: '',
    features: [], specifications: [], variants: [],
    images: [], mainImage: '',
    seoTitle: '', seoDescription: '', seoSlug: '', seoKeywords: '',
    tags: [], targetMarket: '',
    launchDate: '', eolDate: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (visible) {
      if (initialData) {
        setForm({ ...emptyForm, ...initialData, features: initialData.features || [], specifications: initialData.specifications || [], variants: initialData.variants || [], images: initialData.images || [], tags: initialData.tags || [] });
      } else {
        setForm(emptyForm);
      }
      setActiveSection('basic');
    }
  }, [visible, initialData]);

  const update = (field, value) => setForm({ ...form, [field]: value });

  // ── Calculated fields ─────────────────────────────────────────────────────
  const margin = useMemo(() => {
    const p = parseFloat(form.price) || 0;
    const c = parseFloat(form.cost) || 0;
    if (p <= 0) return null;
    return Math.round(((p - c) / p) * 100);
  }, [form.price, form.cost]);

  const profit = useMemo(() => {
    const p = parseFloat(form.salePrice || form.price) || 0;
    const c = parseFloat(form.cost) || 0;
    return p - c;
  }, [form.price, form.salePrice, form.cost]);

  const stockStatus = useMemo(() => {
    const s = parseFloat(form.stock);
    const t = parseFloat(form.lowStockThreshold) || 0;
    if (isNaN(s)) return null;
    if (s === 0) return { label: 'Out of Stock', color: COLORS.danger };
    if (s <= t) return { label: 'Low Stock', color: COLORS.warning };
    return { label: 'In Stock', color: COLORS.success };
  }, [form.stock, form.lowStockThreshold]);

  const validate = () => {
    if (!form.name.trim()) { Alert.alert('Required', 'Product name is required'); setActiveSection('basic'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const generateDescription = async () => {
    if (!form.name) { Alert.alert('Name required', 'Enter a product name first'); return; }
    setAiGenerating(true);
    try {
      const r = await api.post(EP.aiGenerate, {
        prompt: `Write a compelling product description for "${form.name}" (${form.category}, ${form.type}). Highlight key benefits, target audience: ${form.targetMarket || 'general'}.`,
        tone: 'professional', length: 'medium',
      }).catch(() => null);
      const text = r?.data?.content || r?.data?.text || '';
      if (text) {
        update('description', text);
      } else {
        const fallback = `${form.name} is a ${String(form.category ?? '').toLowerCase()} ${String(form.type ?? '').toLowerCase()} designed for ${form.targetMarket || 'modern businesses'}.\n\nBuilt with quality and reliability in mind, ${form.name} delivers exceptional value through:\n\n• Industry-leading features\n• Intuitive design\n• Reliable performance\n• Comprehensive support\n\nWhether you're just getting started or scaling up, ${form.name} adapts to your needs.`;
        update('description', fallback);
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // ── Item helpers ──────────────────────────────────────────────────────────
  const addFeature = () => {
    const f = newFeatureText.trim();
    if (!f) return;
    update('features', [...form.features, f]);
    setNewFeatureText('');
  };
  const removeFeature = (i) => update('features', form.features.filter((_, idx) => idx !== i));

  const addTag = () => {
    const t = newTagText.trim();
    if (!t || form.tags.includes(t)) return;
    update('tags', [...form.tags, t]);
    setNewTagText('');
  };
  const removeTag = (t) => update('tags', form.tags.filter(x => x !== t));

  const addSpec = () => {
    if (!newSpecKey.trim() || !newSpecValue.trim()) return;
    update('specifications', [...form.specifications, { id: Date.now(), key: newSpecKey.trim(), value: newSpecValue.trim() }]);
    setNewSpecKey(''); setNewSpecValue('');
  };
  const removeSpec = (id) => update('specifications', form.specifications.filter(s => s.id !== id));

  const addVariant = () => {
    if (!newVariantName.trim()) return;
    update('variants', [...form.variants, { id: Date.now(), name: newVariantName.trim(), price: newVariantPrice || form.price, stock: 0 }]);
    setNewVariantName(''); setNewVariantPrice('');
  };
  const updateVariant = (id, field, value) => update('variants', form.variants.map(v => v.id === id ? { ...v, [field]: value } : v));
  const removeVariant = (id) => update('variants', form.variants.filter(v => v.id !== id));

  const addImage = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    update('images', [...form.images, { id: Date.now(), url, alt: '' }]);
    if (!form.mainImage) update('mainImage', url);
    setNewImageUrl('');
  };
  const removeImage = (id) => update('images', form.images.filter(i => i.id !== id));
  const setMainImage = (url) => update('mainImage', url);

  // ── Renderers ─────────────────────────────────────────────────────────────
  const renderBasic = () => (
    <>
      <Field label="Product Name" required value={form.name} onChange={(v) => { update('name', v); if (!form.seoSlug) update('seoSlug', slugify(v)); }} placeholder="e.g. JobEng Pro" />
      <Field label="Short Description" value={form.shortDesc} onChange={(v) => update('shortDesc', v)} placeholder="One-line tagline" />

      <View style={st.row}>
        <Label style={{ flex: 1 }}>Full Description</Label>
        <TouchableOpacity style={[st.smallBtn, { backgroundColor: COLORS.accent }, aiGenerating && { backgroundColor: COLORS.light }]} onPress={generateDescription} disabled={aiGenerating}>
          {aiGenerating ? <ActivityIndicator color="#FFF" size="small" /> : (
            <>
              <MaterialCommunityIcons name="sparkles" size={14} color="#FFF" />
              <Text style={st.smallBtnTxt}>AI</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      <TextInput style={[st.input, { minHeight: 120, textAlignVertical: 'top', marginTop: 6 }]} value={form.description} onChangeText={(v) => update('description', v)} placeholder="Detailed product description..." placeholderTextColor={COLORS.light} multiline />

      <Label>Category</Label>
      <ChipRow items={PRODUCT_CATEGORIES} selected={form.category} onSelect={(v) => update('category', v)} color={COLORS.accent} />

      <Label>Type</Label>
      <ChipRow items={PRODUCT_TYPES} selected={form.type} onSelect={(v) => update('type', v)} color={COLORS.accent} />

      <Label>Status</Label>
      <ChipRow items={PRODUCT_STATUSES} selected={form.status} onSelect={(v) => update('status', v)} color={COLORS.accent} />

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Version" value={form.version} onChange={(v) => update('version', v)} placeholder="1.0.0" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="SKU" value={form.sku} onChange={(v) => update('sku', v)} placeholder="PROD-001" />
        </View>
      </View>

      <Field label="Target Market" value={form.targetMarket} onChange={(v) => update('targetMarket', v)} placeholder="Small businesses, enterprises, etc." />

      <Field label="Tags" value={newTagText} onChange={setNewTagText} placeholder="Add tag and press +" rightIcon="plus" onRightPress={addTag} />
      <View style={st.chipsArea}>
        {form.tags.map(t => (
          <TouchableOpacity key={t} style={st.tagChip} onPress={() => removeTag(t)}>
            <Text style={st.tagChipTxt}>#{t}</Text>
            <MaterialCommunityIcons name="close-circle" size={14} color={COLORS.sub} />
          </TouchableOpacity>
        ))}
      </View>
    </>
  );

  const renderPricing = () => (
    <>
      <Label>Currency</Label>
      <ChipRow items={['GBP', 'USD', 'EUR', 'AED', 'SAR']} selected={form.currency} onSelect={(v) => update('currency', v)} color={COLORS.success} />

      <Label>Billing Cycle</Label>
      <ChipRow items={BILLING_CYCLES} selected={form.billingCycle} onSelect={(v) => update('billingCycle', v)} color={COLORS.success} />

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Regular Price" value={form.price} onChange={(v) => update('price', v)} placeholder="0.00" keyboardType="decimal-pad" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Sale Price" value={form.salePrice} onChange={(v) => update('salePrice', v)} placeholder="0.00" keyboardType="decimal-pad" />
        </View>
      </View>
      <Field label="Unit Cost" value={form.cost} onChange={(v) => update('cost', v)} placeholder="0.00" keyboardType="decimal-pad" />

      {(form.price || form.cost) && (
        <View style={st.calcRow}>
          {margin !== null && (
            <View style={[st.calcCard, { backgroundColor: margin < 0 ? `${COLORS.danger}15` : margin < 20 ? `${COLORS.warning}15` : `${COLORS.success}15` }]}>
              <Text style={[st.calcNum, { color: margin < 0 ? COLORS.danger : margin < 20 ? COLORS.warning : COLORS.success }]}>{margin}%</Text>
              <Text style={st.calcLbl}>Margin</Text>
            </View>
          )}
          <View style={st.calcCard}>
            <Text style={[st.calcNum, { color: profit < 0 ? COLORS.danger : COLORS.success }]}>{formatCurrency(profit, form.currency)}</Text>
            <Text style={st.calcLbl}>Profit / unit</Text>
          </View>
        </View>
      )}

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Launch Date" value={form.launchDate} onChange={(v) => update('launchDate', v)} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="End-of-Life" value={form.eolDate} onChange={(v) => update('eolDate', v)} placeholder="YYYY-MM-DD" />
        </View>
      </View>
    </>
  );

  const renderInventory = () => (
    <>
      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Stock Quantity" value={form.stock} onChange={(v) => update('stock', v)} placeholder="0" keyboardType="numeric" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Low Stock Alert" value={form.lowStockThreshold} onChange={(v) => update('lowStockThreshold', v)} placeholder="5" keyboardType="numeric" />
        </View>
      </View>

      {stockStatus && (
        <View style={[st.statusCard, { backgroundColor: `${stockStatus.color}15`, marginTop: 8 }]}>
          <MaterialCommunityIcons name="package-variant" size={22} color={stockStatus.color} />
          <Text style={[st.statusTxt, { color: stockStatus.color }]}>{stockStatus.label}</Text>
        </View>
      )}

      <View style={st.row}>
        <View style={{ flex: 1, marginRight: 6 }}>
          <Field label="Weight (kg)" value={form.weight} onChange={(v) => update('weight', v)} placeholder="0.0" keyboardType="decimal-pad" />
        </View>
        <View style={{ flex: 1, marginLeft: 6 }}>
          <Field label="Dimensions" value={form.dimensions} onChange={(v) => update('dimensions', v)} placeholder="L × W × H cm" />
        </View>
      </View>

      <Label style={{ marginTop: 16 }}>Features</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newFeatureText} onChangeText={setNewFeatureText} placeholder="Add a feature..." placeholderTextColor={COLORS.light} onSubmitEditing={addFeature} />
        <TouchableOpacity style={st.iconBtn} onPress={addFeature}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.accent} />
        </TouchableOpacity>
      </View>
      {form.features.map((f, i) => (
        <View key={i} style={st.listRow}>
          <MaterialCommunityIcons name="check-circle" size={18} color={COLORS.success} />
          <Text style={[st.listTitle, { flex: 1, marginLeft: 10 }]}>{f}</Text>
          <TouchableOpacity onPress={() => removeFeature(i)}>
            <MaterialCommunityIcons name="close" size={18} color={COLORS.sub} />
          </TouchableOpacity>
        </View>
      ))}

      <Label style={{ marginTop: 16 }}>Specifications</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newSpecKey} onChangeText={setNewSpecKey} placeholder="Key (e.g. Material)" placeholderTextColor={COLORS.light} />
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newSpecValue} onChangeText={setNewSpecValue} placeholder="Value" placeholderTextColor={COLORS.light} />
        <TouchableOpacity style={st.iconBtn} onPress={addSpec}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.accent} />
        </TouchableOpacity>
      </View>
      {form.specifications.map(s => (
        <View key={s.id} style={[st.listRow, { backgroundColor: COLORS.bgAlt, paddingHorizontal: 12, borderRadius: 8 }]}>
          <Text style={[st.listTitle, { fontWeight: '700' }]}>{s.key}:</Text>
          <Text style={[st.listTitle, { flex: 1, marginLeft: 8, color: COLORS.sub }]}>{s.value}</Text>
          <TouchableOpacity onPress={() => removeSpec(s.id)}>
            <MaterialCommunityIcons name="close" size={18} color={COLORS.sub} />
          </TouchableOpacity>
        </View>
      ))}
    </>
  );

  const renderVariants = () => (
    <>
      <Label>Product Variants</Label>
      <Text style={st.helper}>Create variations (sizes, colors, configurations) with different prices and stock</Text>

      <View style={[st.row, { marginTop: 12 }]}>
        <TextInput style={[st.input, { flex: 2, marginRight: 6 }]} value={newVariantName} onChangeText={setNewVariantName} placeholder="Variant (e.g. Large, Blue)" placeholderTextColor={COLORS.light} />
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newVariantPrice} onChangeText={setNewVariantPrice} placeholder="Price" placeholderTextColor={COLORS.light} keyboardType="decimal-pad" />
        <TouchableOpacity style={st.iconBtn} onPress={addVariant}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.accent} />
        </TouchableOpacity>
      </View>

      {form.variants.map(v => (
        <View key={v.id} style={st.variantCard}>
          <View style={{ flex: 1 }}>
            <TextInput style={st.variantName} value={v.name} onChangeText={(t) => updateVariant(v.id, 'name', t)} placeholder="Variant name" placeholderTextColor={COLORS.light} />
            <View style={[st.row, { marginTop: 6 }]}>
              <View style={{ flex: 1, marginRight: 6 }}>
                <Text style={st.helper}>Price</Text>
                <TextInput style={[st.input, { fontSize: 13, padding: 8 }]} value={String(v.price || '')} onChangeText={(t) => updateVariant(v.id, 'price', t)} placeholder="0.00" placeholderTextColor={COLORS.light} keyboardType="decimal-pad" />
              </View>
              <View style={{ flex: 1, marginLeft: 6 }}>
                <Text style={st.helper}>Stock</Text>
                <TextInput style={[st.input, { fontSize: 13, padding: 8 }]} value={String(v.stock || 0)} onChangeText={(t) => updateVariant(v.id, 'stock', t)} placeholder="0" placeholderTextColor={COLORS.light} keyboardType="numeric" />
              </View>
            </View>
          </View>
          <TouchableOpacity onPress={() => removeVariant(v.id)} style={{ marginLeft: 8 }}>
            <MaterialCommunityIcons name="delete-outline" size={22} color={COLORS.danger} />
          </TouchableOpacity>
        </View>
      ))}

      {form.variants.length === 0 && (
        <View style={[st.empty, { padding: 24 }]}>
          <MaterialCommunityIcons name="palette-swatch" size={40} color={COLORS.border} />
          <Text style={st.helper}>No variants yet</Text>
        </View>
      )}
    </>
  );

  const renderMedia = () => (
    <>
      <Label>Product Images</Label>
      <View style={st.row}>
        <TextInput style={[st.input, { flex: 1, marginRight: 6 }]} value={newImageUrl} onChangeText={setNewImageUrl} placeholder="https://image-url.jpg" placeholderTextColor={COLORS.light} autoCapitalize="none" />
        <TouchableOpacity style={st.iconBtn} onPress={addImage}>
          <MaterialCommunityIcons name="plus" size={20} color={COLORS.accent} />
        </TouchableOpacity>
      </View>

      {form.images.map(img => (
        <View key={img.id} style={st.imageRow}>
          <View style={[st.imagePreview, img.url === form.mainImage && { borderColor: COLORS.accent, borderWidth: 2 }]}>
            <MaterialCommunityIcons name="image" size={28} color={COLORS.light} />
          </View>
          <View style={{ flex: 1, marginHorizontal: 10 }}>
            <Text style={st.listTitle} numberOfLines={1}>{img.url}</Text>
            <TextInput style={[st.input, { fontSize: 12, padding: 6, marginTop: 4 }]} value={img.alt} onChangeText={(t) => update('images', form.images.map(i => i.id === img.id ? { ...i, alt: t } : i))} placeholder="Alt text" placeholderTextColor={COLORS.light} />
          </View>
          <View>
            {img.url !== form.mainImage && (
              <TouchableOpacity onPress={() => setMainImage(img.url)} style={{ marginBottom: 8 }}>
                <MaterialCommunityIcons name="star-outline" size={20} color={COLORS.warning} />
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => removeImage(img.id)}>
              <MaterialCommunityIcons name="delete-outline" size={20} color={COLORS.danger} />
            </TouchableOpacity>
          </View>
        </View>
      ))}

      {form.images.length === 0 && (
        <View style={[st.empty, { padding: 24 }]}>
          <MaterialCommunityIcons name="image-multiple-outline" size={40} color={COLORS.border} />
          <Text style={st.helper}>Add image URLs to build your gallery</Text>
        </View>
      )}
    </>
  );

  const renderSEO = () => (
    <>
      <Field label="SEO Slug" value={form.seoSlug} onChange={(v) => update('seoSlug', v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="product-url-slug" />
      <Field label="Meta Title" value={form.seoTitle} onChange={(v) => update('seoTitle', v)} placeholder={form.name || "Page title for SEO"} maxLength={60} />
      <Text style={st.helper}>{(form.seoTitle || '').length} / 60 — under 60 recommended</Text>

      <Field label="Meta Description" value={form.seoDescription} onChange={(v) => update('seoDescription', v)} placeholder="Short summary for search results" multiline rows={3} maxLength={160} />
      <Text style={st.helper}>{(form.seoDescription || '').length} / 160</Text>

      <Field label="Keywords" value={form.seoKeywords} onChange={(v) => update('seoKeywords', v)} placeholder="product, category, keyword" />

      <View style={st.previewBox}>
        <Text style={st.previewLbl}>SEARCH PREVIEW</Text>
        <Text style={st.previewTitle}>{form.seoTitle || form.name || 'Product name'}</Text>
        <Text style={st.previewUrl}>example.com/products/{form.seoSlug || 'slug'}</Text>
        <Text style={st.previewDesc}>{form.seoDescription || form.shortDesc || 'Description will appear here...'}</Text>
      </View>
    </>
  );

  const sections = [
    { id: 'basic',     label: 'Basic',     icon: 'information-outline' },
    { id: 'pricing',   label: 'Pricing',   icon: 'cash-multiple' },
    { id: 'inventory', label: 'Inventory', icon: 'package-variant' },
    { id: 'variants',  label: 'Variants',  icon: 'palette-swatch' },
    { id: 'media',     label: 'Media',     icon: 'image-multiple' },
    { id: 'seo',       label: 'SEO',       icon: 'magnify-scan' },
  ];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalHeader}>
            <TouchableOpacity onPress={onClose}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={st.modalTitle}>{initialData ? 'Edit Product' : 'New Product'}</Text>
            <TouchableOpacity onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color={COLORS.accent} /> : <Text style={[st.saveBtn, { color: COLORS.accent }]}>Save</Text>}
            </TouchableOpacity>
          </View>

          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.sectionTabs} contentContainerStyle={{ paddingHorizontal: 12 }}>
            {sections.map(s => (
              <TouchableOpacity key={s.id} style={[st.sectionTab, activeSection === s.id && { backgroundColor: `${COLORS.accent}15` }]} onPress={() => setActiveSection(s.id)}>
                <MaterialCommunityIcons name={s.icon} size={16} color={activeSection === s.id ? COLORS.accent : COLORS.sub} />
                <Text style={[st.sectionTabTxt, activeSection === s.id && { color: COLORS.accent, fontWeight: '700' }]}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
            {activeSection === 'basic'     && renderBasic()}
            {activeSection === 'pricing'   && renderPricing()}
            {activeSection === 'inventory' && renderInventory()}
            {activeSection === 'variants'  && renderVariants()}
            {activeSection === 'media'     && renderMedia()}
            {activeSection === 'seo'       && renderSEO()}
          </ScrollView>
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SHARED FORM COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════
const Field = ({ label, required, value, onChange, placeholder, multiline, rows, keyboardType, rightIcon, onRightPress, onSubmit, maxLength }) => (
  <View style={{ marginBottom: 14 }}>
    {label ? <Text style={st.label}>{label}{required && <Text style={{ color: COLORS.danger }}> *</Text>}</Text> : null}
    <View style={rightIcon ? { flexDirection: 'row', alignItems: 'center' } : null}>
      <TextInput
        style={[st.input, multiline && { minHeight: 28 * (rows || 4), textAlignVertical: 'top' }, rightIcon && { flex: 1, marginRight: 6 }]}
        value={String(value || '')}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={COLORS.light}
        multiline={multiline}
        keyboardType={keyboardType}
        onSubmitEditing={onSubmit}
        maxLength={maxLength}
      />
      {rightIcon && (
        <TouchableOpacity style={st.iconBtn} onPress={onRightPress}>
          <MaterialCommunityIcons name={rightIcon} size={20} color={COLORS.primary} />
        </TouchableOpacity>
      )}
    </View>
  </View>
);

const Label = ({ children, style }) => <Text style={[st.label, { marginTop: 4, marginBottom: 6 }, style]}>{children}</Text>;

const ChipRow = ({ items, selected, onSelect, color, size }) => (
  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4 }}>
    {items.map(item => (
      <TouchableOpacity
        key={item}
        style={[st.chip, size === 'small' && st.chipSmall, selected === item && { backgroundColor: color, borderColor: color }]}
        onPress={() => onSelect(item)}
      >
        <Text style={[st.chipTxt, size === 'small' && { fontSize: 11 }, selected === item && { color: '#FFF', fontWeight: '700' }]}>{item}</Text>
      </TouchableOpacity>
    ))}
  </ScrollView>
);

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN — list + tabs + filter + bulk
// ══════════════════════════════════════════════════════════════════════════════
export default function ProjectProductCreationScreen({ navigation, route }) {
  const initialType = route?.params?.type || 'projects';
  const [activeTab, setActiveTab]         = useState(initialType);
  const [projects, setProjects]           = useState([]);
  const [products, setProducts]           = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [refreshing, setRefreshing]       = useState(false);
  const [companyId, setCompanyId]         = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAnalytics, setShowAnalytics]       = useState(false);
  const [showFilterSheet, setShowFilterSheet]   = useState(false);
  const [showBulkSheet, setShowBulkSheet]       = useState(false);
  const [editingItem, setEditingItem]           = useState(null);

  const [searchQuery, setSearchQuery]     = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy]               = useState('updated_desc');
  const [selectedIds, setSelectedIds]     = useState(new Set());

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setIsLoading(true);
      const b = route?.params?.company_id || (await getBusinessId());
      setCompanyId(b);
      await fetchAll(b);
    } catch (e) {
      console.error('init:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => { setRefreshing(true); await fetchAll(companyId); setRefreshing(false); };

  const fetchAll = async (bizId) => {
    const cid = parseInt(bizId, 10) || 0;
    if (!cid) return;
    const p = { company_id: cid };

    const [profileRes, projRes, prodRes] = await Promise.allSettled([
      api.get(EP.companyProfile(cid)),
      api.get(EP.projects, { params: p }).catch(() => ({ data: { success: false } })),
      api.get(EP.products, { params: p }).catch(() => ({ data: { success: false } })),
    ]);

    if (profileRes.status === 'fulfilled' && profileRes.value?.data?.success) {
      const pf = Array.isArray(profileRes.value.data.data) ? profileRes.value.data.data[0] : profileRes.value.data.data;
      setCompanyProfile(pf);
    }
    setProjects(projRes.status === 'fulfilled' ? safeData(projRes.value) : []);
    setProducts(prodRes.status === 'fulfilled' ? safeData(prodRes.value) : []);
  };

  // ── Save / Delete ─────────────────────────────────────────────────────────
  const saveProject = async (data) => {
    const cid = parseInt(companyId, 10) || 0;
    const payload = { ...data, company_id: cid, updated_at: new Date().toISOString() };
    try {
      let saved;
      if (data.id && !String(data.id).startsWith('local_')) {
        const r = await api.put(`${EP.projects}/${data.id}`, payload).catch(() => null);
        saved = r?.data?.success ? r.data.data : { ...payload, local_only: true };
      } else {
        const r = await api.post(EP.projects, { ...payload, created_at: new Date().toISOString() }).catch(() => null);
        saved = r?.data?.success ? r.data.data : { ...payload, id: `local_${Date.now()}`, created_at: new Date().toISOString(), local_only: true };
      }
      if (data.id) {
        setProjects(projects.map(p => p.id === data.id ? saved : p));
      } else {
        setProjects([saved, ...projects]);
      }
      setShowProjectModal(false);
      setEditingItem(null);
      Alert.alert('Saved', `Project "${saved.name}" saved.`);
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const saveProduct = async (data) => {
    const cid = parseInt(companyId, 10) || 0;
    const payload = { ...data, company_id: cid, updated_at: new Date().toISOString() };
    try {
      let saved;
      if (data.id && !String(data.id).startsWith('local_')) {
        const r = await api.put(`${EP.products}/${data.id}`, payload).catch(() => null);
        saved = r?.data?.success ? r.data.data : { ...payload, local_only: true };
      } else {
        const r = await api.post(EP.products, { ...payload, created_at: new Date().toISOString() }).catch(() => null);
        saved = r?.data?.success ? r.data.data : { ...payload, id: `local_${Date.now()}`, created_at: new Date().toISOString(), local_only: true };
      }
      if (data.id) {
        setProducts(products.map(p => p.id === data.id ? saved : p));
      } else {
        setProducts([saved, ...products]);
      }
      setShowProductModal(false);
      setEditingItem(null);
      Alert.alert('Saved', `Product "${saved.name}" saved.`);
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  };

  const deleteItem = (item) => {
    const itemType = activeTab === 'projects' ? 'project' : 'product';
    const endpoint = activeTab === 'projects' ? EP.projects : EP.products;
    Alert.alert(`Delete ${itemType}?`, `Permanently delete "${item.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          if (activeTab === 'projects') setProjects(projects.filter(p => p.id !== item.id));
          else setProducts(products.filter(p => p.id !== item.id));
          try { await api.delete(`${endpoint}/${item.id}`); } catch {}
        },
      },
    ]);
  };

  const duplicateItem = (item) => {
    const copy = { ...item, id: undefined, name: `${item.name} (copy)`, status: activeTab === 'projects' ? 'Planning' : 'Concept' };
    setEditingItem(copy);
    if (activeTab === 'projects') setShowProjectModal(true);
    else setShowProductModal(true);
  };

  // ── Filter / sort ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let items = activeTab === 'projects' ? projects : products;

    if (statusFilter !== 'all') items = items.filter(i => i.status === statusFilter);
    if (activeTab === 'projects' && priorityFilter !== 'all') items = items.filter(i => i.priority === priorityFilter);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i => JSON.stringify(i).toLowerCase().includes(q));
    }

    // Sort
    const sorted = [...items];
    switch (sortBy) {
      case 'updated_desc': sorted.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)); break;
      case 'updated_asc':  sorted.sort((a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0)); break;
      case 'name_asc':     sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      case 'name_desc':    sorted.sort((a, b) => (b.name || '').localeCompare(a.name || '')); break;
      case 'budget_desc':  sorted.sort((a, b) => (parseFloat(b.budget || b.price) || 0) - (parseFloat(a.budget || a.price) || 0)); break;
    }
    return sorted;
  }, [activeTab, projects, products, statusFilter, priorityFilter, searchQuery, sortBy]);

  // ── Bulk ops ──────────────────────────────────────────────────────────────
  const toggleSelection = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const bulkDelete = () => {
    Alert.alert(`Delete ${selectedIds.size} items?`, 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete All', style: 'destructive',
        onPress: async () => {
          const ids = Array.from(selectedIds);
          const endpoint = activeTab === 'projects' ? EP.projects : EP.products;
          if (activeTab === 'projects') setProjects(projects.filter(p => !selectedIds.has(p.id)));
          else setProducts(products.filter(p => !selectedIds.has(p.id)));
          setSelectedIds(new Set());
          setShowBulkSheet(false);
          try { await Promise.all(ids.map(id => api.delete(`${endpoint}/${id}`).catch(() => null))); } catch {}
        },
      },
    ]);
  };

  const bulkStatus = async (newStatus) => {
    const endpoint = activeTab === 'projects' ? EP.projects : EP.products;
    const ids = Array.from(selectedIds);
    if (activeTab === 'projects') setProjects(projects.map(p => selectedIds.has(p.id) ? { ...p, status: newStatus } : p));
    else setProducts(products.map(p => selectedIds.has(p.id) ? { ...p, status: newStatus } : p));
    setSelectedIds(new Set());
    setShowBulkSheet(false);
    try { await Promise.all(ids.map(id => api.patch(`${endpoint}/${id}`, { status: newStatus }).catch(() => null))); } catch {}
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={[COLORS.primary, COLORS.primaryDk]} style={st.screenHeader}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.headerBtn}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.screenTitle}>{companyProfile?.name || 'Projects & Products'}</Text>
          <Text style={st.screenSub}>{projects.length} projects · {products.length} products</Text>
        </View>
        <TouchableOpacity onPress={() => setShowAnalytics(true)} style={st.headerBtn}>
          <MaterialCommunityIcons name="chart-bar" size={22} color="#FFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={st.mainTabs}>
      <TouchableOpacity style={[st.mainTab, activeTab === 'projects' && { backgroundColor: COLORS.primary }]} onPress={() => setActiveTab('projects')}>
        <MaterialCommunityIcons name="briefcase-outline" size={18} color={activeTab === 'projects' ? '#FFF' : COLORS.sub} />
        <Text style={[st.mainTabTxt, activeTab === 'projects' && { color: '#FFF', fontWeight: '700' }]}>Projects ({projects.length})</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[st.mainTab, activeTab === 'products' && { backgroundColor: COLORS.accent }]} onPress={() => setActiveTab('products')}>
        <MaterialCommunityIcons name="package-variant" size={18} color={activeTab === 'products' ? '#FFF' : COLORS.sub} />
        <Text style={[st.mainTabTxt, activeTab === 'products' && { color: '#FFF', fontWeight: '700' }]}>Products ({products.length})</Text>
      </TouchableOpacity>
    </View>
  );

  const renderToolbar = () => (
    <View style={st.toolbar}>
      <View style={st.searchRow}>
        <MaterialCommunityIcons name="magnify" size={18} color={COLORS.light} />
        <TextInput style={st.searchInput} placeholder={`Search ${activeTab}...`} placeholderTextColor={COLORS.light} value={searchQuery} onChangeText={setSearchQuery} />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={16} color={COLORS.light} />
          </TouchableOpacity>
        ) : null}
      </View>
      <TouchableOpacity style={st.toolBtn} onPress={() => setShowFilterSheet(true)}>
        <MaterialCommunityIcons name="filter-variant" size={20} color={(statusFilter !== 'all' || priorityFilter !== 'all') ? (activeTab === 'projects' ? COLORS.primary : COLORS.accent) : COLORS.sub} />
      </TouchableOpacity>
      {selectedIds.size > 0 && (
        <TouchableOpacity style={[st.toolBtn, { backgroundColor: `${activeTab === 'projects' ? COLORS.primary : COLORS.accent}15` }]} onPress={() => setShowBulkSheet(true)}>
          <Text style={{ color: activeTab === 'projects' ? COLORS.primary : COLORS.accent, fontWeight: '700' }}>{selectedIds.size}</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderProjectCard = (p) => {
    const isSelected = selectedIds.has(p.id);
    const statusColor = STATUS_COLORS[p.status] || COLORS.sub;
    const priorityColor = STATUS_COLORS[p.priority] || COLORS.sub;
    return (
      <TouchableOpacity
        key={p.id}
        style={[st.card, isSelected && { borderColor: COLORS.primary, borderWidth: 2 }]}
        onPress={() => {
          if (selectedIds.size > 0) toggleSelection(p.id);
          else { setEditingItem(p); setShowProjectModal(true); }
        }}
        onLongPress={() => toggleSelection(p.id)}
      >
        <View style={st.cardTop}>
          <View style={{ flex: 1 }}>
            <Text style={st.cardName} numberOfLines={1}>{p.name}</Text>
            {p.client ? <Text style={st.cardClient}>{p.client}</Text> : null}
          </View>
          <View style={[st.statusBadge, { backgroundColor: `${statusColor}20` }]}>
            <Text style={[st.statusBadgeTxt, { color: statusColor }]}>{p.status}</Text>
          </View>
        </View>

        {p.description ? <Text style={st.cardDesc} numberOfLines={2}>{p.description}</Text> : null}

        <View style={st.progressBar}>
          <View style={[st.progressFill, { width: `${p.progress || 0}%`, backgroundColor: COLORS.primary }]} />
        </View>
        <View style={st.cardMetrics}>
          <Text style={st.cardMetric}>{p.progress || 0}% complete</Text>
          <View style={[st.priorityDot, { backgroundColor: priorityColor }]} />
          <Text style={st.cardMetric}>{p.priority}</Text>
        </View>

        <View style={st.cardFooter}>
          <Text style={st.cardFooterTxt}>
            <MaterialCommunityIcons name="calendar" size={11} color={COLORS.sub} /> {formatDate(p.endDate || p.deadline)}
          </Text>
          {(p.budget || p.budget === 0) ? (
            <Text style={st.cardFooterTxt}>
              <MaterialCommunityIcons name="cash" size={11} color={COLORS.sub} /> {formatCurrency(p.budget, p.currency)}
            </Text>
          ) : null}
          {p.local_only && <View style={st.localBadge}><Text style={st.localBadgeTxt}>LOCAL</Text></View>}
        </View>
      </TouchableOpacity>
    );
  };

  const renderProductCard = (p) => {
    const isSelected = selectedIds.has(p.id);
    const statusColor = STATUS_COLORS[p.status] || COLORS.sub;
    return (
      <TouchableOpacity
        key={p.id}
        style={[st.card, isSelected && { borderColor: COLORS.accent, borderWidth: 2 }]}
        onPress={() => {
          if (selectedIds.size > 0) toggleSelection(p.id);
          else { setEditingItem(p); setShowProductModal(true); }
        }}
        onLongPress={() => toggleSelection(p.id)}
      >
        <View style={st.cardTop}>
          <View style={{ flex: 1 }}>
            <Text style={st.cardName} numberOfLines={1}>{p.name}</Text>
            <Text style={st.cardClient}>{p.category} · v{p.version || '1.0'}</Text>
          </View>
          <View style={[st.statusBadge, { backgroundColor: `${statusColor}20` }]}>
            <Text style={[st.statusBadgeTxt, { color: statusColor }]}>{p.status}</Text>
          </View>
        </View>

        {p.shortDesc || p.description ? (
          <Text style={st.cardDesc} numberOfLines={2}>{p.shortDesc || p.description}</Text>
        ) : null}

        <View style={st.cardFooter}>
          <Text style={[st.cardPrice, { color: COLORS.accent }]}>{formatCurrency(p.price, p.currency)}</Text>
          {p.stock !== undefined && p.stock !== '' && (
            <Text style={st.cardFooterTxt}>Stock: {p.stock}</Text>
          )}
          {p.local_only && <View style={st.localBadge}><Text style={st.localBadgeTxt}>LOCAL</Text></View>}
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmpty = () => (
    <View style={st.empty}>
      <MaterialCommunityIcons name={activeTab === 'projects' ? 'briefcase-outline' : 'package-variant'} size={64} color={COLORS.border} />
      <Text style={st.emptyTitle}>No {activeTab} yet</Text>
      <Text style={st.emptySub}>Create your first {activeTab.slice(0, -1)} to get started</Text>
      <TouchableOpacity
        style={[st.emptyBtn, { backgroundColor: activeTab === 'projects' ? COLORS.primary : COLORS.accent }]}
        onPress={() => { setEditingItem(null); activeTab === 'projects' ? setShowProjectModal(true) : setShowProductModal(true); }}
      >
        <MaterialCommunityIcons name="plus" size={18} color="#FFF" />
        <Text style={st.emptyBtnTxt}>Create {activeTab.slice(0, -1)}</Text>
      </TouchableOpacity>
    </View>
  );

  // ── Analytics Modal ───────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const projTotalBudget = projects.reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
    const projTotalActual = projects.reduce((s, p) => s + (parseFloat(p.actualCost) || 0), 0);
    const productRevenue = products.reduce((s, p) => s + ((parseFloat(p.price) || 0) * (parseFloat(p.stock) || 0)), 0);
    const projByStatus = PROJECT_STATUSES.map(s => ({ status: s, count: projects.filter(p => p.status === s).length }));
    const prodByStatus = PRODUCT_STATUSES.map(s => ({ status: s, count: products.filter(p => p.status === s).length }));

    return (
      <Modal visible={showAnalytics} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAnalytics(false)}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalHeader}>
            <TouchableOpacity onPress={() => setShowAnalytics(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={st.modalTitle}>Analytics</Text>
            <View style={{ width: 26 }} />
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={st.anaTitle}>Project Pipeline</Text>
            <View style={st.statGrid}>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.primary }]}>{projects.length}</Text>
                <Text style={st.statLbl}>Total Projects</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.success }]}>{projects.filter(p => p.status === 'Active').length}</Text>
                <Text style={st.statLbl}>Active</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.success }]}>{formatCurrency(projTotalBudget)}</Text>
                <Text style={st.statLbl}>Total Budget</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: projTotalActual > projTotalBudget ? COLORS.danger : COLORS.warning }]}>{formatCurrency(projTotalActual)}</Text>
                <Text style={st.statLbl}>Spent</Text>
              </View>
            </View>

            <Text style={[st.anaTitle, { marginTop: 20 }]}>Projects by Status</Text>
            {projByStatus.map(s => (
              <View key={s.status} style={st.barRow}>
                <Text style={st.barLbl}>{s.status}</Text>
                <View style={st.barBg}>
                  <View style={[st.barFill, { width: `${projects.length ? (s.count / projects.length) * 100 : 0}%`, backgroundColor: STATUS_COLORS[s.status] || COLORS.sub }]} />
                </View>
                <Text style={st.barCnt}>{s.count}</Text>
              </View>
            ))}

            <Text style={[st.anaTitle, { marginTop: 20 }]}>Product Portfolio</Text>
            <View style={st.statGrid}>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.accent }]}>{products.length}</Text>
                <Text style={st.statLbl}>Total Products</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.success }]}>{products.filter(p => p.status === 'Active').length}</Text>
                <Text style={st.statLbl}>Active</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.warning }]}>{products.filter(p => p.status === 'Development').length}</Text>
                <Text style={st.statLbl}>In Development</Text>
              </View>
              <View style={st.statCard}>
                <Text style={[st.statNum, { color: COLORS.success }]}>{formatCurrency(productRevenue)}</Text>
                <Text style={st.statLbl}>Inventory Value</Text>
              </View>
            </View>

            <Text style={[st.anaTitle, { marginTop: 20 }]}>Products by Status</Text>
            {prodByStatus.map(s => (
              <View key={s.status} style={st.barRow}>
                <Text style={st.barLbl}>{s.status}</Text>
                <View style={st.barBg}>
                  <View style={[st.barFill, { width: `${products.length ? (s.count / products.length) * 100 : 0}%`, backgroundColor: STATUS_COLORS[s.status] || COLORS.sub }]} />
                </View>
                <Text style={st.barCnt}>{s.count}</Text>
              </View>
            ))}
            <View style={{ height: 32 }} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Filter sheet ──────────────────────────────────────────────────────────
  const renderFilterSheet = () => {
    const statuses = activeTab === 'projects' ? PROJECT_STATUSES : PRODUCT_STATUSES;
    return (
      <Modal visible={showFilterSheet} animationType="fade" transparent onRequestClose={() => setShowFilterSheet(false)}>
        <Pressable style={st.backdrop} onPress={() => setShowFilterSheet(false)}>
          <Pressable style={st.sheet}>
            <Text style={st.sheetTitle}>Filter & Sort</Text>

            <Text style={[st.label, { marginTop: 16 }]}>Status</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4 }}>
              <TouchableOpacity style={[st.chip, statusFilter === 'all' && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setStatusFilter('all')}>
                <Text style={[st.chipTxt, statusFilter === 'all' && { color: '#FFF', fontWeight: '700' }]}>All</Text>
              </TouchableOpacity>
              {statuses.map(s => (
                <TouchableOpacity key={s} style={[st.chip, statusFilter === s && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setStatusFilter(s)}>
                  <Text style={[st.chipTxt, statusFilter === s && { color: '#FFF', fontWeight: '700' }]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {activeTab === 'projects' && (
              <>
                <Text style={[st.label, { marginTop: 16 }]}>Priority</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4 }}>
                  <TouchableOpacity style={[st.chip, priorityFilter === 'all' && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setPriorityFilter('all')}>
                    <Text style={[st.chipTxt, priorityFilter === 'all' && { color: '#FFF', fontWeight: '700' }]}>All</Text>
                  </TouchableOpacity>
                  {PRIORITIES.map(p => (
                    <TouchableOpacity key={p} style={[st.chip, priorityFilter === p && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setPriorityFilter(p)}>
                      <Text style={[st.chipTxt, priorityFilter === p && { color: '#FFF', fontWeight: '700' }]}>{p}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </>
            )}

            <Text style={[st.label, { marginTop: 16 }]}>Sort By</Text>
            {[
              { id: 'updated_desc', label: 'Recently updated' },
              { id: 'updated_asc',  label: 'Oldest first' },
              { id: 'name_asc',     label: 'Name (A → Z)' },
              { id: 'name_desc',    label: 'Name (Z → A)' },
              { id: 'budget_desc',  label: activeTab === 'projects' ? 'Budget (high → low)' : 'Price (high → low)' },
            ].map(s => (
              <TouchableOpacity key={s.id} style={[st.sortRow, sortBy === s.id && { backgroundColor: `${COLORS.primary}10` }]} onPress={() => setSortBy(s.id)}>
                <MaterialCommunityIcons name={sortBy === s.id ? 'radiobox-marked' : 'radiobox-blank'} size={20} color={sortBy === s.id ? COLORS.primary : COLORS.sub} />
                <Text style={[st.sortRowTxt, sortBy === s.id && { color: COLORS.primary, fontWeight: '700' }]}>{s.label}</Text>
              </TouchableOpacity>
            ))}

            <TouchableOpacity style={[st.applyBtn, { backgroundColor: COLORS.primary }]} onPress={() => setShowFilterSheet(false)}>
              <Text style={{ color: '#FFF', fontWeight: '700' }}>Apply</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    );
  };

  // ── Bulk sheet ────────────────────────────────────────────────────────────
  const renderBulkSheet = () => (
    <Modal visible={showBulkSheet} animationType="fade" transparent onRequestClose={() => setShowBulkSheet(false)}>
      <Pressable style={st.backdrop} onPress={() => setShowBulkSheet(false)}>
        <Pressable style={st.sheet}>
          <Text style={st.sheetTitle}>{selectedIds.size} selected</Text>
          <TouchableOpacity style={st.bulkOpt} onPress={() => setSelectedIds(new Set(filtered.map(i => i.id)))}>
            <MaterialCommunityIcons name="select-all" size={20} color={COLORS.primary} />
            <Text style={st.bulkOptTxt}>Select All</Text>
          </TouchableOpacity>
          <TouchableOpacity style={st.bulkOpt} onPress={() => bulkStatus(activeTab === 'projects' ? 'Active' : 'Active')}>
            <MaterialCommunityIcons name="play-circle-outline" size={20} color={COLORS.success} />
            <Text style={st.bulkOptTxt}>Mark Active</Text>
          </TouchableOpacity>
          <TouchableOpacity style={st.bulkOpt} onPress={() => bulkStatus(activeTab === 'projects' ? 'On Hold' : 'Sunset')}>
            <MaterialCommunityIcons name="pause-circle-outline" size={20} color={COLORS.warning} />
            <Text style={st.bulkOptTxt}>{activeTab === 'projects' ? 'Put On Hold' : 'Sunset'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={st.bulkOpt} onPress={() => bulkStatus(activeTab === 'projects' ? 'Completed' : 'Discontinued')}>
            <MaterialCommunityIcons name="archive-outline" size={20} color={COLORS.sub} />
            <Text style={st.bulkOptTxt}>{activeTab === 'projects' ? 'Mark Complete' : 'Discontinue'}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={st.bulkOpt} onPress={bulkDelete}>
            <MaterialCommunityIcons name="delete-outline" size={20} color={COLORS.danger} />
            <Text style={[st.bulkOptTxt, { color: COLORS.danger }]}>Delete</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.bulkOpt, { borderTopWidth: 1, borderTopColor: COLORS.border, marginTop: 4 }]} onPress={() => { setSelectedIds(new Set()); setShowBulkSheet(false); }}>
            <MaterialCommunityIcons name="close" size={20} color={COLORS.sub} />
            <Text style={st.bulkOptTxt}>Clear Selection</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={st.loading}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={st.loadingTxt}>Loading...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primary} />
      {renderHeader()}
      {renderTabs()}
      {renderToolbar()}

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingVertical: 8 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[activeTab === 'projects' ? COLORS.primary : COLORS.accent]} />}
        showsVerticalScrollIndicator={false}
      >
        {filtered.length === 0 ? renderEmpty() : (
          activeTab === 'projects' ? filtered.map(renderProjectCard) : filtered.map(renderProductCard)
        )}
        <View style={{ height: 90 }} />
      </ScrollView>

      <TouchableOpacity
        style={[st.fab, { backgroundColor: activeTab === 'projects' ? COLORS.primary : COLORS.accent }]}
        onPress={() => { setEditingItem(null); activeTab === 'projects' ? setShowProjectModal(true) : setShowProductModal(true); }}
      >
        <MaterialCommunityIcons name="plus" size={28} color="#FFF" />
      </TouchableOpacity>

      <ProjectCreationModal visible={showProjectModal} onClose={() => { setShowProjectModal(false); setEditingItem(null); }} onSave={saveProject} initialData={editingItem} />
      <ProductCreationModal visible={showProductModal} onClose={() => { setShowProductModal(false); setEditingItem(null); }} onSave={saveProduct} initialData={editingItem} />
      {renderAnalytics()}
      {renderFilterSheet()}
      {renderBulkSheet()}
    </SafeAreaView>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg },
  loadingTxt: { fontSize: 14, color: COLORS.sub, marginTop: 12 },

  // Header
  screenHeader: { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  screenTitle: { fontSize: 20, fontWeight: '700', color: '#FFF' },
  screenSub: { fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 },

  // Main tabs
  mainTabs: { flexDirection: 'row', padding: 8, backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border, gap: 8 },
  mainTab: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 10, backgroundColor: COLORS.bgAlt, gap: 6 },
  mainTabTxt: { fontSize: 13, color: COLORS.sub, fontWeight: '500' },

  // Toolbar
  toolbar: { flexDirection: 'row', alignItems: 'center', padding: 12, gap: 8, backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  searchRow: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bg, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, gap: 6 },
  searchInput: { flex: 1, fontSize: 14, color: COLORS.text },
  toolBtn: { width: 40, height: 36, borderRadius: 10, backgroundColor: COLORS.bg, justifyContent: 'center', alignItems: 'center' },

  // Card
  card: { backgroundColor: COLORS.surface, marginHorizontal: 12, marginVertical: 4, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border },
  cardTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  cardName: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  cardClient: { fontSize: 12, color: COLORS.sub, marginTop: 2 },
  cardDesc: { fontSize: 12, color: COLORS.sub, lineHeight: 16, marginBottom: 8 },
  cardMetrics: { flexDirection: 'row', alignItems: 'center', marginTop: 8, gap: 8 },
  cardMetric: { fontSize: 11, color: COLORS.sub, fontWeight: '500' },
  cardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: COLORS.bgAlt, gap: 12 },
  cardFooterTxt: { fontSize: 11, color: COLORS.sub },
  cardPrice: { fontSize: 15, fontWeight: '700' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  statusBadgeTxt: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  priorityDot: { width: 8, height: 8, borderRadius: 4 },
  localBadge: { paddingHorizontal: 5, paddingVertical: 2, borderRadius: 4, backgroundColor: COLORS.warning },
  localBadgeTxt: { fontSize: 8, color: '#FFF', fontWeight: '700' },

  // Progress
  progressBar: { height: 4, backgroundColor: COLORS.border, borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 2 },

  // Empty
  empty: { alignItems: 'center', padding: 40, marginTop: 40 },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, marginTop: 12 },
  emptySub: { fontSize: 13, color: COLORS.sub, marginTop: 4, textAlign: 'center' },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, gap: 6, marginTop: 16 },
  emptyBtnTxt: { color: '#FFF', fontWeight: '700' },

  // FAB
  fab: { position: 'absolute', bottom: 20, right: 16, width: 56, height: 56, borderRadius: 28, justifyContent: 'center', alignItems: 'center', elevation: 4, shadowColor: '#000', shadowOpacity: 0.2, shadowOffset: { width: 0, height: 2 }, shadowRadius: 4 },

  // Modal
  modal: { flex: 1, backgroundColor: COLORS.surface },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  modalTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1, marginHorizontal: 12 },
  saveBtn: { fontSize: 16, fontWeight: '700' },

  // Section tabs
  sectionTabs: { flexGrow: 0, flexShrink: 0, backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border, paddingVertical: 6 },
  sectionTab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, marginRight: 6, borderRadius: 16, gap: 6 },
  sectionTabTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },

  // Form
  label: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginBottom: 6 },
  helper: { fontSize: 11, color: COLORS.sub, marginTop: 4 },
  input: { backgroundColor: COLORS.bgAlt, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: COLORS.text, borderWidth: 1, borderColor: COLORS.border },
  row: { flexDirection: 'row', alignItems: 'flex-start' },

  // Chips
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.bgAlt, marginRight: 6 },
  chipSmall: { paddingHorizontal: 10, paddingVertical: 5 },
  chipTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },
  chipsArea: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  tagChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: `${COLORS.primary}15`, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 14, gap: 4 },
  tagChipTxt: { fontSize: 12, color: COLORS.primary, fontWeight: '600' },
  memberChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bgAlt, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 16, gap: 6 },
  memberAvatar: { width: 22, height: 22, borderRadius: 11, backgroundColor: COLORS.primary, justifyContent: 'center', alignItems: 'center' },
  memberName: { fontSize: 12, color: COLORS.text, fontWeight: '500' },
  miniChip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: COLORS.bgAlt, marginRight: 6 },
  miniChipTxt: { fontSize: 11, color: COLORS.sub, fontWeight: '600' },

  // Icon button
  iconBtn: { width: 44, height: 44, borderRadius: 10, backgroundColor: COLORS.bgAlt, justifyContent: 'center', alignItems: 'center' },
  smallBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, gap: 4 },
  smallBtnTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },

  // List rows
  listRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 4, borderBottomWidth: 1, borderBottomColor: COLORS.bgAlt },
  listTitle: { fontSize: 14, color: COLORS.text },
  listMeta: { fontSize: 11, color: COLORS.sub, marginTop: 2 },

  // Risk cards
  riskCard: { backgroundColor: COLORS.bgAlt, borderRadius: 10, padding: 12, marginTop: 8, borderLeftWidth: 3, borderLeftColor: COLORS.warning },

  // Variant cards
  variantCard: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: COLORS.bgAlt, borderRadius: 10, padding: 12, marginTop: 8 },
  variantName: { fontSize: 14, fontWeight: '600', color: COLORS.text, padding: 4 },

  // Image rows
  imageRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bgAlt, borderRadius: 10, padding: 8, marginTop: 8 },
  imagePreview: { width: 50, height: 50, borderRadius: 8, backgroundColor: COLORS.border, justifyContent: 'center', alignItems: 'center' },

  // Status & calc cards
  statusCard: { flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 10, gap: 10 },
  statusTxt: { fontSize: 14, fontWeight: '700' },
  calcRow: { flexDirection: 'row', gap: 8, marginTop: 8 },
  calcCard: { flex: 1, backgroundColor: COLORS.bgAlt, padding: 12, borderRadius: 10, alignItems: 'center' },
  calcNum: { fontSize: 18, fontWeight: '700', color: COLORS.text },
  calcLbl: { fontSize: 11, color: COLORS.sub, marginTop: 4, textAlign: 'center' },

  // Preview box (SEO)
  previewBox: { backgroundColor: COLORS.bgAlt, padding: 12, borderRadius: 10, marginTop: 12, borderLeftWidth: 3, borderLeftColor: COLORS.success },
  previewLbl: { fontSize: 10, color: COLORS.sub, fontWeight: '700', letterSpacing: 0.5, marginBottom: 6 },
  previewTitle: { fontSize: 15, color: '#1a0dab', fontWeight: '500' },
  previewUrl: { fontSize: 12, color: COLORS.success, marginTop: 2 },
  previewDesc: { fontSize: 12, color: COLORS.text, lineHeight: 17, marginTop: 4 },

  // Analytics
  anaTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 },
  statGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  statCard: { width: (SCREEN_W - 48) / 2, backgroundColor: COLORS.bgAlt, padding: 14, borderRadius: 10, alignItems: 'center' },
  statNum: { fontSize: 20, fontWeight: '700' },
  statLbl: { fontSize: 11, color: COLORS.sub, marginTop: 4, textAlign: 'center' },
  barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  barLbl: { fontSize: 12, color: COLORS.text, width: 90 },
  barBg: { flex: 1, height: 6, backgroundColor: COLORS.border, borderRadius: 3, marginHorizontal: 8, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  barCnt: { fontSize: 12, color: COLORS.text, width: 24, textAlign: 'right', fontWeight: '600' },

  // Backdrop & sheet
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: COLORS.surface, padding: 16, borderTopLeftRadius: 22, borderTopRightRadius: 22, paddingBottom: 32, maxHeight: '80%' },
  sheetTitle: { fontSize: 16, fontWeight: '700', color: COLORS.text },

  // Sort & bulk
  sortRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 8, borderRadius: 8, gap: 10, marginTop: 2 },
  sortRowTxt: { fontSize: 14, color: COLORS.text },
  applyBtn: { marginTop: 16, padding: 14, borderRadius: 10, alignItems: 'center' },
  bulkOpt: { flexDirection: 'row', alignItems: 'center', padding: 14, gap: 10 },
  bulkOptTxt: { fontSize: 14, color: COLORS.text, fontWeight: '500' },
});
